export const exercises = [
  // ── § 1 PatG: Patentfähigkeit & Erfindungsbegriff ──────────────────────────

  {
    id: "da-patg1-001",
    topic: "§ 1 PatG — Patentfähigkeit",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg1"],
    title: "Voraussetzungen der Patentierbarkeit",
    prompt:
      "Welche vier kumulativen Voraussetzungen müssen nach § 1 PatG vorliegen, damit eine Erfindung patentierbar ist?",
    options: [
      {
        text: "Erfindung, Neuheit, erfinderische Tätigkeit, gewerbliche Anwendbarkeit",
        correct: true,
      },
      {
        text: "Erfindung, Neuheit, technischer Fortschritt, gewerbliche Anwendbarkeit",
        correct: false,
      },
      {
        text: "Neuheit, erfinderische Tätigkeit, gewerbliche Anwendbarkeit, schriftliche Offenbarung",
        correct: false,
      },
      {
        text: "Erfindung, Neuheit, Nichtoffensichtlichkeit, technische Wirkung",
        correct: false,
      },
    ],
    explanation:
      "§ 1 Abs. 1 PatG verlangt kumulativ: eine Erfindung, die neu ist, auf erfinderischer Tätigkeit beruht und gewerblich anwendbar ist. 'Technischer Fortschritt' und 'Nichtoffensichtlichkeit' sind keine Tatbestandsmerkmale des deutschen PatG.",
  },

  {
    id: "da-patg1-002",
    topic: "§ 1 PatG — Erfindungsbegriff",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg1", "bghRoteTaube"],
    title: "Technizitätserfordernis",
    prompt:
      "Was ist nach der BGH-Rechtsprechung (Rote Taube) das entscheidende Kriterium für den Erfindungsbegriff im deutschen Patentrecht?",
    options: [
      {
        text: "Die Lehre zum technischen Handeln — eine Anweisung, die Naturkräfte gezielt zur Herbeiführung eines kausal übersehbaren Erfolgs einsetzt",
        correct: true,
      },
      {
        text: "Eine neuartige chemische Verbindung oder Verfahren zur Herstellung derselben",
        correct: false,
      },
      {
        text: "Jede reproducierbare Anweisung, die einen wirtschaftlichen Vorteil erbringt",
        correct: false,
      },
      {
        text: "Ein körperlicher Gegenstand, der in einer industriellen Anlage hergestellt wird",
        correct: false,
      },
    ],
    explanation:
      "BGH Rote Taube (1969) definierte die Erfindung als 'Lehre zum technischen Handeln': eine Anweisung, Naturkräfte unmittelbar (ohne menschliche Denkarbeit als Zwischenstufe) zur Erzielung eines kausal übersehbaren Erfolgs einzusetzen. Dieses Technizitätskriterium ist die Grundlage für die Abgrenzung patentierbarer Erfindungen.",
  },

  {
    id: "da-patg1-003",
    topic: "§ 1 PatG — Technizität",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg1"],
    title: "Prüfung der Technizität",
    prompt:
      "Ein Anmelder beansprucht ein neues Buchführungssystem, das auf einer mathematischen Methode zur Optimierung von Kontenströmen basiert. Die Software wird auf Standardhardware ausgeführt. Wie ist die Technizität zu beurteilen?",
    options: [
      {
        text: "Keine Erfindung, weil der beanspruchte Gegenstand als solcher eine Methode für geschäftliche Tätigkeiten und ein mathematisches Verfahren ist",
        correct: true,
      },
      {
        text: "Erfindung, weil Software auf Hardware ausgeführt wird und damit technischen Charakter hat",
        correct: false,
      },
      {
        text: "Erfindung, wenn die Software nachweislich eine technische Wirkung erzielt",
        correct: false,
      },
      {
        text: "Keine Erfindung ausschließlich wegen fehlender Neuheit",
        correct: false,
      },
    ],
    explanation:
      "§ 1 Abs. 3 Nr. 3 PatG schließt Methoden für geschäftliche Tätigkeiten, § 1 Abs. 3 Nr. 1 schließt mathematische Verfahren vom Patentschutz aus — gemäß § 1 Abs. 4 'als solche'. Der bloße Lauf auf Hardware macht aus einer reinen Buchhaltungsmethode keine technische Erfindung.",
  },

  {
    id: "da-patg1-004",
    topic: "§ 1 Abs. 3/4 PatG — Negativkatalog",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "multi",
    level: 2,
    sources: ["patg1"],
    title: "Negativkatalog § 1 Abs. 3 PatG",
    prompt:
      "Welche der folgenden Gegenstände sind nach § 1 Abs. 3 PatG 'als solche' vom Patentschutz ausgeschlossen? (Mehrere Antworten möglich)",
    options: [
      { text: "Entdeckungen sowie wissenschaftliche Theorien und mathematische Methoden", correct: true },
      { text: "Pläne, Regeln und Verfahren für gedankliche Tätigkeiten, für Spiele oder für geschäftliche Tätigkeiten sowie Programme für Datenverarbeitungsanlagen", correct: true },
      { text: "Wiedergabe von Informationen", correct: true },
      { text: "Verfahren zur Herstellung chemischer Verbindungen", correct: false },
    ],
    explanation:
      "§ 1 Abs. 3 PatG nennt abschließend: (1) Entdeckungen, wissenschaftliche Theorien, mathematische Methoden; (2) ästhetische Formschöpfungen; (3) Pläne, Regeln und Verfahren für gedankliche Tätigkeiten, Spiele, geschäftliche Tätigkeiten sowie Computerprogramme als solche; (4) Wiedergabe von Informationen. Chemische Verfahren sind grundsätzlich patentierbar.",
  },

  {
    id: "da-patg1-005",
    topic: "§ 1 Abs. 3/4 PatG — 'Als solche'",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg1"],
    title: "Bedeutung des 'als solche'-Vorbehalts",
    prompt:
      "Was bedeutet der Vorbehalt 'als solche' in § 1 Abs. 4 PatG für Computerprogramme?",
    options: [
      {
        text: "Ein Computerprogramm ist als solches nicht patentierbar; ein technisches Verfahren, das durch Software implementiert wird und einen technischen Charakter aufweist, kann jedoch patentierbar sein",
        correct: true,
      },
      {
        text: "Computerprogramme sind generell und ausnahmslos vom Patentschutz ausgeschlossen",
        correct: false,
      },
      {
        text: "Software ist nur patentierbar, wenn sie auf spezieller, nicht handelsüblicher Hardware läuft",
        correct: false,
      },
      {
        text: "'Als solche' bedeutet, dass Computerprogramme immer über den Urheberrechtsschutz zu schützen sind",
        correct: false,
      },
    ],
    explanation:
      "Der 'als solche'-Vorbehalt öffnet die Tür für computerbezogene Erfindungen: Wenn ein Programm einen technischen Charakter hat (z.B. Steuerung einer Maschine, verbesserte Datenkompression mit technischer Wirkung), ist es nicht 'als solches' ausgeschlossen. Reine Algorithmen ohne technische Wirkung bleiben ausgeschlossen.",
  },

  {
    id: "da-patg1-006",
    topic: "§ 1 Abs. 3 PatG — Entdeckung vs. Erfindung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg1"],
    title: "Abgrenzung Entdeckung / Erfindung",
    prompt:
      "Ein Forscher stellt fest, dass ein bisher bekannter Wirkstoff X eine bisher unbekannte antibiotische Wirkung gegen Bakterienstamm Y hat. Er beansprucht: (a) den Wirkstoff X als solchen, (b) die Verwendung von X zur Behandlung von Infektionen durch Y. Was ist patentierbar?",
    options: [
      {
        text: "Nur (b) — die Verwendung ist eine technische Lehre; (a) scheitert als bloße Entdeckung einer vorhandenen Eigenschaft",
        correct: true,
      },
      {
        text: "Beide — sowohl der Wirkstoff als auch die Verwendung sind neu und erfinderisch",
        correct: false,
      },
      {
        text: "Keines — biologische Entdeckungen sind generell nicht patentierbar",
        correct: false,
      },
      {
        text: "Nur (a) — der Wirkstoff als Substanz ist das eigentliche neue Element",
        correct: false,
      },
    ],
    explanation:
      "Die Entdeckung einer neuen Eigenschaft eines bekannten Stoffes ist als solche nicht patentierbar (§ 1 Abs. 3 Nr. 1 PatG). Die zweckgebundene Verwendungserfindung hingegen enthält eine technische Lehre (Anwendung eines Naturstoffs in einem neuen technischen Zusammenhang) und kann patentierbar sein.",
  },

  // ── § 1a PatG: Biotechnologische Erfindungen ───────────────────────────────

  {
    id: "da-patg1-007",
    topic: "§ 1a PatG — Biotechnologie",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg1a"],
    title: "§ 1a PatG — biologisches Material",
    prompt:
      "Welche Aussage zu § 1a PatG ist korrekt?",
    options: [
      {
        text: "Biologisches Material, das durch ein technisches Verfahren aus seiner natürlichen Umgebung isoliert oder hergestellt wird, kann Gegenstand einer Erfindung sein, auch wenn es in der Natur bereits existiert",
        correct: true,
      },
      {
        text: "Natürlich vorkommendes biologisches Material ist grundsätzlich von jeglichem Patentschutz ausgeschlossen, unabhängig vom Herstellungsverfahren",
        correct: false,
      },
      {
        text: "§ 1a PatG gilt nur für tierisches, nicht für pflanzliches biologisches Material",
        correct: false,
      },
      {
        text: "Gene des menschlichen Körpers können nur durch Patente am EPA, nicht durch deutsche Patente geschützt werden",
        correct: false,
      },
    ],
    explanation:
      "§ 1a Abs. 1 PatG setzt die Biopatent-Richtlinie (98/44/EG) um: Biologisches Material, das isoliert oder durch technisches Verfahren gewonnen wird, kann Gegenstand einer Erfindung sein — auch wenn es in der Natur vorkommt. Entscheidend ist der technische Eingriff zur Isolation/Herstellung.",
  },

  {
    id: "da-patg1-008",
    topic: "§ 1a PatG — Menschliches Erbgut",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg1a"],
    title: "Patentschutz für menschliche Gene",
    prompt:
      "Unter welcher Bedingung ist nach § 1a Abs. 3 PatG der Schutz einer isolierten Gensequenz des menschlichen Körpers möglich?",
    options: [
      {
        text: "Wenn die gewerbliche Nutzung der Sequenz in der Patentanmeldung konkret offenbart ist",
        correct: true,
      },
      {
        text: "Wenn die Sequenz bisher unbekannt war und durch DNA-Sequenzierung entdeckt wurde",
        correct: false,
      },
      {
        text: "Niemals — menschliche Gensequenzen sind generell von der Patentierbarkeit ausgeschlossen",
        correct: false,
      },
      {
        text: "Wenn der Anmelder die Sequenz aus einer biologischen Probe isoliert hat, ohne staatliche Förderung",
        correct: false,
      },
    ],
    explanation:
      "§ 1a Abs. 3 PatG: Die gewerbliche Anwendbarkeit einer Gensequenz muss in der Patentanmeldung konkret beschrieben sein. Eine bloße Sequenzangabe ohne technische Anwendung reicht nicht. Dies verhindert 'Fishing Expeditions' auf Gensequenzen ohne konkreten Nutzungsplan.",
  },

  // ── § 2 PatG: Ordre public / Sittenwidrigkeit ──────────────────────────────

  {
    id: "da-patg1-009",
    topic: "§ 2 PatG — Ordre public",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg2"],
    title: "§ 2 PatG — Sittenwidrigkeit",
    prompt:
      "Wann ist eine Erfindung nach § 2 PatG von der Patentierbarkeit ausgeschlossen?",
    options: [
      {
        text: "Wenn ihre Veröffentlichung oder Verwertung gegen die öffentliche Ordnung oder die guten Sitten verstoßen würde",
        correct: true,
      },
      {
        text: "Wenn die Erfindung ausschließlich für militärische Zwecke verwendbar ist",
        correct: false,
      },
      {
        text: "Wenn der Erfinder eine strafbare Handlung begangen hat, um die Erfindung zu entwickeln",
        correct: false,
      },
      {
        text: "Wenn die Erfindung wirtschaftlich unrentabel erscheint",
        correct: false,
      },
    ],
    explanation:
      "§ 2 Abs. 1 PatG: Patente werden nicht erteilt für Erfindungen, deren Verwertung gegen die öffentliche Ordnung (ordre public) oder die guten Sitten verstoßen würde. Maßgeblich ist die Verwertung, nicht die bloße Entwicklung. Der Verstoß muss evident sein — nicht jeder ethische Einwand reicht.",
  },

  {
    id: "da-patg1-010",
    topic: "§ 2 PatG — Ausschlüsse",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "multi",
    level: 2,
    sources: ["patg2"],
    title: "§ 2 PatG — Regelbeispiele",
    prompt:
      "Welche Gegenstände sind nach § 2 Abs. 2 PatG ausdrücklich von der Patentierbarkeit ausgeschlossen? (Mehrere Antworten möglich)",
    options: [
      { text: "Verfahren zum Klonen von menschlichen Lebewesen", correct: true },
      { text: "Verfahren zur Veränderung der genetischen Identität der Keimbahn des menschlichen Lebewesens", correct: true },
      { text: "Verwendung von menschlichen Embryonen zu industriellen oder kommerziellen Zwecken", correct: true },
      { text: "Gentechnisch veränderte Pflanzensorten", correct: false },
    ],
    explanation:
      "§ 2 Abs. 2 PatG nennt abschließende Regelbeispiele: (1) Klonierung menschlicher Lebewesen, (2) Veränderung der Keimbahnidentität, (3) Verwendung menschlicher Embryonen zu industriellen/kommerziellen Zwecken, (4) Verfahren zur Veränderung der genetischen Identität von Tieren, die Leiden verursachen ohne wesentlichen medizinischen Nutzen. Gentechnisch veränderte Pflanzen sind dagegen grundsätzlich nicht per se ausgeschlossen (vgl. § 2a).",
  },

  // ── § 2a PatG: Pflanzensorten / Tierarten / biologische Verfahren ──────────

  {
    id: "da-patg1-011",
    topic: "§ 2a PatG — Pflanzensorten und Tierarten",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg2a"],
    title: "§ 2a PatG — Abgrenzung Sorte / Erfindung",
    prompt:
      "Ein Unternehmen entwickelt durch gentechnische Methoden eine Weizenvarietät mit erhöhter Trockenheitsresistenz. Es beansprucht (a) die spezifische Weizensorte X und (b) alle gentechnisch modifizierten Getreidepflanzen, die ein bestimmtes Resistenzgen enthalten. Was ist patentierbar?",
    options: [
      {
        text: "Nur (b) — Pflanzensorten als solche sind nach § 2a Abs. 1 PatG ausgeschlossen; ein breiter biotechnologischer Anspruch auf gentechnisch modifizierte Pflanzen kann jedoch patentierbar sein",
        correct: true,
      },
      {
        text: "Beide — biotechnologische Methoden überbrücken den Ausschluss für Pflanzensorten vollständig",
        correct: false,
      },
      {
        text: "Keines — der gesamte Bereich Pflanzenzüchtung ist patentrechtlich nicht schutzfähig",
        correct: false,
      },
      {
        text: "Nur (a) — nur die konkrete Sorte ist ausreichend definiert, um patentierbar zu sein",
        correct: false,
      },
    ],
    explanation:
      "§ 2a Abs. 1 Nr. 1 PatG schließt Pflanzensorten (und Tierarten) als solche aus. Biotechnologische Erfindungen, die sich auf Pflanzen beziehen, aber auf mehr als eine Sorte anwendbar sind (breiter Schutzbereich), können dagegen patentierbar sein. Maßgeblich ist, ob der Anspruch eine bestimmte Sorte oder ein technisches Prinzip schützt.",
  },

  {
    id: "da-patg1-012",
    topic: "§ 2a PatG — Im Wesentlichen biologische Verfahren",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg2a"],
    title: "Im Wesentlichen biologisches Verfahren",
    prompt:
      "Was ist ein 'im Wesentlichen biologisches Verfahren' im Sinne von § 2a Abs. 1 Nr. 2 PatG?",
    options: [
      {
        text: "Ein Verfahren zur Züchtung von Pflanzen oder Tieren, das vollständig auf natürlichen Phänomenen wie Kreuzung oder Selektion beruht",
        correct: true,
      },
      {
        text: "Jedes Verfahren, das biologische Organismen involviert, einschließlich mikrobiologischer Verfahren",
        correct: false,
      },
      {
        text: "Ein Verfahren, das in einem biologischen Labor durchgeführt wird",
        correct: false,
      },
      {
        text: "Verfahren, die ohne chemische Reagenzien auskommen",
        correct: false,
      },
    ],
    explanation:
      "§ 2a Abs. 1 Nr. 2 PatG schließt im Wesentlichen biologische Verfahren zur Züchtung von Pflanzen oder Tieren aus — also solche, die vollständig auf natürlichen Vorgängen (Kreuzung, Selektion) beruhen. Mikrobiologische Verfahren sind ausdrücklich ausgenommen (§ 2a Abs. 2 Nr. 1) und damit patentierbar.",
  },

  // ── § 3 PatG: Neuheit ──────────────────────────────────────────────────────

  {
    id: "da-patg1-013",
    topic: "§ 3 PatG — Neuheit",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg3"],
    title: "Begriff des Stands der Technik",
    prompt:
      "Was umfasst der 'Stand der Technik' nach § 3 Abs. 1 PatG?",
    options: [
      {
        text: "Alle Kenntnisse, die vor dem Anmeldetag der Öffentlichkeit durch schriftliche oder mündliche Beschreibung, durch Benutzung oder in sonstiger Weise zugänglich gemacht worden sind",
        correct: true,
      },
      {
        text: "Nur schriftlich veröffentlichte Dokumente, die dem zuständigen Fachmann bekannt sind",
        correct: false,
      },
      {
        text: "Alle Patentanmeldungen und -schriften, die vor dem Anmeldetag veröffentlicht wurden",
        correct: false,
      },
      {
        text: "Kenntnisse, die in wissenschaftlichen Fachzeitschriften vor dem Anmeldetag publiziert wurden",
        correct: false,
      },
    ],
    explanation:
      "§ 3 Abs. 1 PatG definiert den Stand der Technik sehr weit: alle Kenntnisse, die vor dem Anmeldetag (maßgeblicher Zeitpunkt) der Öffentlichkeit zugänglich gemacht worden sind — durch Schrift, Rede, Benutzung oder auf sonstige Weise. Zugänglichkeit, nicht tatsächliche Kenntnis des Anmelders oder Fachmanns, ist entscheidend.",
  },

  {
    id: "da-patg1-014",
    topic: "§ 3 PatG — Vorveröffentlichter SdT",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg3"],
    title: "Vorveröffentlichter vs. nachveröffentlichter Stand der Technik",
    prompt:
      "Worin liegt der entscheidende Unterschied zwischen vorveröffentlichtem (§ 3 Abs. 1) und nachveröffentlichtem Stand der Technik (§ 3 Abs. 2) im deutschen Patentrecht?",
    options: [
      {
        text: "Vorveröffentlichter SdT ist vor dem Anmeldetag zugänglich gemacht worden und ist für alle Patentierbarkeitsvoraussetzungen relevant; nachveröffentlichter SdT (Patentanmeldungen mit früherem Anmeldetag) ist nur für die Neuheitsprüfung relevant",
        correct: true,
      },
      {
        text: "Nachveröffentlichter SdT gilt nur für EPÜ-Verfahren, nicht für deutsche Patente",
        correct: false,
      },
      {
        text: "Nachveröffentlichter SdT ist für alle Patentierbarkeitsvoraussetzungen relevant, vorveröffentlichter SdT nur für die Neuheit",
        correct: false,
      },
      {
        text: "Beide Arten des SdT sind für alle Patentierbarkeitsvoraussetzungen gleichermaßen relevant",
        correct: false,
      },
    ],
    explanation:
      "Vorveröffentlichter SdT (§ 3 Abs. 1) schadet der Neuheit und der erfinderischen Tätigkeit. Nachveröffentlichter SdT (§ 3 Abs. 2) sind nationale/europäische Patentanmeldungen mit früherem Anmeldetag (aber späterer Veröffentlichung) — diese schaden ausschließlich der Neuheit, nicht der erfinderischen Tätigkeit (kein 'Neuheitsgift' für § 4).",
  },

  {
    id: "da-patg1-015",
    topic: "§ 3 PatG — Neuheitsprüfung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg3"],
    title: "Neuheitsprüfung — Einzeldokument",
    prompt:
      "Nach welchem Grundsatz wird die Neuheit im deutschen Patentrecht geprüft?",
    options: [
      {
        text: "Jedes Dokument des SdT wird einzeln gegen den Anspruch gehalten ('Einzelvergleich'); eine Kombination mehrerer Dokumente ist für die Neuheitsprüfung unzulässig",
        correct: true,
      },
      {
        text: "Der SdT wird als Ganzes berücksichtigt; die Neuheit fehlt, wenn der beanspruchte Gegenstand aus mehreren Dokumenten herleitbar ist",
        correct: false,
      },
      {
        text: "Die Neuheit richtet sich nach dem Wissensstand des zuständigen Fachmanns, der mehrere Dokumente kombinieren kann",
        correct: false,
      },
      {
        text: "Neuheit fehlt, sobald ein einziges Merkmal im SdT offenbart ist",
        correct: false,
      },
    ],
    explanation:
      "Für die Neuheit gilt das Einzelvergleichsprinzip: Ein Gegenstand ist neuheitsschädlich vorweggenommen, wenn ein einzelnes Dokument alle Merkmale des Anspruchs offenbart. Die Kombination mehrerer Dokumente ist nur bei der erfinderischen Tätigkeit (§ 4 PatG / mosaikartige Zusammenschau) zulässig.",
  },

  {
    id: "da-patg1-016",
    topic: "§ 3 PatG — Unschädliche Offenbarungen",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg3"],
    title: "6-Monatsfrist — unschädliche Offenbarungen",
    prompt:
      "Der Erfinder stellt seine Erfindung auf einer Messe aus und reicht drei Monate später die Patentanmeldung ein. Wie wirkt sich die Messeausstellung auf die Neuheit aus?",
    options: [
      {
        text: "Die Messeausstellung ist nach § 3 Abs. 5 PatG unschädlich, wenn die Messe als 'anerkannte internationale Ausstellung' gilt und die Zurückbehaltungsfrist von 6 Monaten eingehalten wurde",
        correct: true,
      },
      {
        text: "Die Messeausstellung ist immer schädlich — der Erfinder hätte erst anmelden müssen",
        correct: false,
      },
      {
        text: "Eigene Offenbarungen des Erfinders sind generell für 6 Monate unschädlich, unabhängig von der Art der Offenbarung",
        correct: false,
      },
      {
        text: "Die Messeausstellung ist nur schädlich, wenn Fotos der Erfindung veröffentlicht wurden",
        correct: false,
      },
    ],
    explanation:
      "§ 3 Abs. 5 PatG sieht eine eng begrenzte Ausnahme vor: Offenbarungen auf anerkannten internationalen Ausstellungen sind innerhalb von 6 Monaten vor dem Anmeldetag unschädlich. Im Gegensatz zum US-amerikanischen Recht gibt es im deutschen/europäischen Recht keine generelle 'Grace Period' für alle Eigenveröffentlichungen.",
  },

  {
    id: "da-patg1-017",
    topic: "§ 3 PatG — Unschädliche Offenbarungen",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg3"],
    title: "Missbrauch als unschädliche Offenbarung",
    prompt:
      "Ein Mitarbeiter des Anmelders veröffentlicht vertrauliches Erfindungsmaterial unbefugt 4 Monate vor der Patentanmeldung. Welche Auswirkung hat dies auf die Neuheit?",
    options: [
      {
        text: "Diese Offenbarung ist nach § 3 Abs. 5 Nr. 2 PatG als Missbrauch zum Nachteil des Anmelders unschädlich, wenn die 6-Monatsfrist eingehalten ist",
        correct: true,
      },
      {
        text: "Die Offenbarung ist schädlich — es hätte eine einstweilige Verfügung erwirkt werden müssen",
        correct: false,
      },
      {
        text: "Die Offenbarung ist nur dann unschädlich, wenn der Missbrauch gerichtlich festgestellt wurde",
        correct: false,
      },
      {
        text: "Missbrauchsoffenbarungen sind grundsätzlich neuheitsschädlich, da das Patentamt die internen Vorgänge nicht prüfen kann",
        correct: false,
      },
    ],
    explanation:
      "§ 3 Abs. 5 Nr. 2 PatG: Offenbarungen, die auf einem offensichtlichen Missbrauch zum Nachteil des Anmelders beruhen, sind innerhalb von 6 Monaten vor dem Anmeldetag neuheitsunschädlich. Die Nachweislast liegt beim Anmelder. Diese Regelung schützt vor unberechtigter Vorveröffentlichung.",
  },

  {
    id: "da-patg1-018",
    topic: "§ 3 PatG — Auswahlerfindung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg3"],
    title: "Neuheit der Auswahlerfindung",
    prompt:
      "Der SdT offenbart eine Verbindungsklasse mit 10⁶ möglichen Verbindungen (breiter Bereich). Der Anmelder beansprucht eine spezifische Untergruppe von 5 Verbindungen mit besonderer technischer Wirkung. Ist der Anspruch neu?",
    options: [
      {
        text: "Ja — eine Auswahl aus einem breiten SdT-Bereich ist neu, sofern die ausgewählten Verbindungen nicht individuell vorweggenommen sind",
        correct: true,
      },
      {
        text: "Nein — alle Verbindungen innerhalb eines offenbarten breiten Bereichs gelten als neuheitsschädlich vorweggenommen",
        correct: false,
      },
      {
        text: "Ja, aber nur wenn die Untergruppe eine überraschende Wirkung aufweist (das ist dann erfinderisch, nicht nur neu)",
        correct: false,
      },
      {
        text: "Nein — Auswahlerfindungen sind im deutschen Patentrecht grundsätzlich ausgeschlossen",
        correct: false,
      },
    ],
    explanation:
      "Auswahlerfindungen sind neu, wenn die spezifische Auswahl nicht individuell im breiten SdT-Bereich offenbart ist. Die bloße Einbeziehung in einen allgemeinen Gattungsbegriff ist keine neuheitsschädliche Offenbarung. Ob die Auswahl auch erfinderisch ist (überraschende Wirkung), ist eine separate Frage.",
  },

  {
    id: "da-patg1-019",
    topic: "§ 3 PatG — Stoffe und Zweckbindung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg3"],
    title: "Zweckgebundener Erzeugnisanspruch",
    prompt:
      "Stoff X ist im SdT bekannt. Ein Anmelder entdeckt, dass X eine bisher unbekannte Eigenschaft Y hat und beansprucht 'Verwendung von X zur Herstellung eines Medikaments zur Behandlung von Krankheit Z'. Was ist der Zweck des zweckgebundenen Erzeugnisanspruchs?",
    options: [
      {
        text: "Er erlaubt den Schutz für eine neue Anwendung eines bekannten Stoffs — der Zweck verleiht dem Anspruch Neuheit, weil Stoff+Zweck zusammen im SdT nicht offenbart waren",
        correct: true,
      },
      {
        text: "Er ist eine reine Umgehungskonstruktion ohne eigenständigen Schutzwert",
        correct: false,
      },
      {
        text: "Er schützt nur das Herstellungsverfahren, nicht den Stoff selbst",
        correct: false,
      },
      {
        text: "Der zweckgebundene Erzeugnisanspruch ist im deutschen Patentrecht nicht anerkannt",
        correct: false,
      },
    ],
    explanation:
      "Der zweckgebundene Erzeugnisanspruch (insbesondere in der Pharmazie: 'swiss-type claim' oder 'purpose-limited product claim') überwindet das Neuheitsproblem bei bekannten Stoffen: Die Neuheit liegt in der neuen Zweckbestimmung. Dies ist für zweite medizinische Indikationen besonders relevant.",
  },

  {
    id: "da-patg1-020",
    topic: "§ 3 PatG — Anmeldetag",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg3"],
    title: "Maßgeblicher Zeitpunkt für Neuheit",
    prompt:
      "Zu welchem Zeitpunkt wird der Stand der Technik für die Neuheitsprüfung nach § 3 PatG bestimmt?",
    options: [
      { text: "Anmeldetag (bzw. Prioritätstag bei wirksamer Prioritätsinanspruchnahme)", correct: true },
      { text: "Offenlegungstag der Patentanmeldung", correct: false },
      { text: "Prüfungstag beim DPMA", correct: false },
      { text: "Patenterteilungstag", correct: false },
    ],
    explanation:
      "Maßgeblicher Zeitpunkt für den SdT ist der Anmeldetag (§ 3 Abs. 1 PatG: 'vor dem für den Anmeldetag maßgeblichen Tag'). Bei wirksamer Prioritätsinanspruchnahme tritt der Prioritätstag an die Stelle des Anmeldetags — das Prioritätsprinzip schiebt den relevanten Stichtag nach vorn.",
  },

  // ── § 4 PatG: Erfinderische Tätigkeit ─────────────────────────────────────

  {
    id: "da-patg1-021",
    topic: "§ 4 PatG — Erfinderische Tätigkeit",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg4"],
    title: "Maßstab erfinderische Tätigkeit",
    prompt:
      "Wessen Fähigkeiten und Kenntnisse sind nach § 4 PatG für die Beurteilung der erfinderischen Tätigkeit maßgeblich?",
    options: [
      { text: "Der zuständige Fachmann — eine Kunstfigur mit durchschnittlichem Fachwissen auf dem relevanten Gebiet", correct: true },
      { text: "Der Durchschnittserfinder — jemand mit überdurchschnittlichen Kenntnissen auf dem Fachgebiet", correct: false },
      { text: "Der Anmelder selbst — seine subjektiven Fähigkeiten zum Zeitpunkt der Erfindung", correct: false },
      { text: "Das Prüfergremium des DPMA mit kollektivem Expertenwissen", correct: false },
    ],
    explanation:
      "§ 4 PatG setzt auf den 'Fachmann auf dem betreffenden Gebiet' als Maßstab. Dies ist eine Kunstfigur: ein Durchschnittsfachmann mit dem normalen Fachwissen seines Gebiets — nicht brillant, aber kompetent. Er kennt den gesamten vorveröffentlichten SdT und kann Routineaufgaben lösen, aber nicht erfinderisch tätig sein.",
  },

  {
    id: "da-patg1-022",
    topic: "§ 4 PatG — Fachmann-Kunstfigur",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "multi",
    level: 2,
    sources: ["patg4", "bghRoteTaube"],
    title: "Eigenschaften des Fachmanns",
    prompt:
      "Welche Eigenschaften kennzeichnen den 'Fachmann' als Prüfungsmaßstab im deutschen/europäischen Patentrecht? (Mehrere Antworten möglich)",
    options: [
      { text: "Er kennt den gesamten einschlägigen Stand der Technik auf seinem Fachgebiet", correct: true },
      { text: "Er ist nicht erfinderisch tätig — er kann Routinearbeiten ausführen, aber keine Erfindungen machen", correct: true },
      { text: "Er kann Fachgebiete kombinieren, sofern dies für ihn naheliegend ist", correct: true },
      { text: "Er ist ein Einzelperson mit Doktortitel auf dem relevanten Fachgebiet", correct: false },
    ],
    explanation:
      "Der Fachmann: (1) kennt den gesamten relevanten SdT, (2) ist nicht erfinderisch — er kann nur routinemäßig tätig sein, (3) kann bei grenzüberschneidenden Problemen auch benachbarte Fachgebiete konsultieren. Er muss keine Einzelperson sein — bei komplexen Gebieten kann es ein 'Fachteam' sein. Kein Doktortitel erforderlich.",
  },

  {
    id: "da-patg1-023",
    topic: "§ 4 PatG — Naheliegen",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg4"],
    title: "Naheliegens-Maßstab",
    prompt:
      "Wann liegt eine Erfindung 'nahe' im Sinne von § 4 PatG?",
    options: [
      {
        text: "Wenn der Fachmann ausgehend vom nächstliegenden SdT durch naheliegende Schritte — ohne erfinderische Überlegungen — zur beanspruchten Lösung gelangt wäre",
        correct: true,
      },
      {
        text: "Wenn die Erfindung rückblickend betrachtet einfach erscheint",
        correct: false,
      },
      {
        text: "Wenn der Erfinder selbst bestätigt, dass die Lösung für ihn einfach war",
        correct: false,
      },
      {
        text: "Wenn die beanspruchte Lösung in einem Lehrbuch beschrieben ist",
        correct: false,
      },
    ],
    explanation:
      "Erfinderische Tätigkeit fehlt, wenn der Fachmann ausgehend vom SdT durch routinemäßige Schritte (ohne Erfindungsschritt) zur Lösung hätte gelangen können. Rückschauende Betrachtung muss vermieden werden — es kommt auf den Wissensstand vor der Erfindung an, nicht auf das Wissen, dass die Erfindung existiert.",
  },

  {
    id: "da-patg1-024",
    topic: "§ 4 PatG — Rückschauende Betrachtung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg4"],
    title: "Ex-post-facto-Analyse vermeiden",
    prompt:
      "Was versteht man unter 'rückschauender Betrachtung' (ex post facto analysis) bei der Prüfung erfinderischer Tätigkeit, und warum ist sie zu vermeiden?",
    options: [
      {
        text: "Die Beurteilung des Naheliegens in Kenntnis der Erfindung selbst — dies führt fälschlicherweise dazu, dass die Erfindung als 'offensichtlich' erscheint, weil man den Weg zum Ergebnis kennt",
        correct: true,
      },
      {
        text: "Die Verwendung von Dokumenten, die nach dem Anmeldetag veröffentlicht wurden",
        correct: false,
      },
      {
        text: "Das Zurückgreifen auf historische Dokumente, die älter als 20 Jahre sind",
        correct: false,
      },
      {
        text: "Die Bewertung einer Erfindung durch Sachverständige, die auf dem Fachgebiet tätig sind",
        correct: false,
      },
    ],
    explanation:
      "Rückschauende Betrachtung (ex-post-facto-Fehler): Kennt man die Erfindung, erscheint der Weg dorthin oft 'selbstverständlich' — man konstruiert rückblickend eine gedankliche Brücke zum Ergebnis. Der Fachmann muss aber ex ante beurteilt werden: Hätte er die Lösung ohne Kenntnis der Erfindung gefunden? Dies ist der häufigste Fehler bei der erfinderischen Tätigkeit.",
  },

  {
    id: "da-patg1-025",
    topic: "§ 4 PatG — Mosaikartige Zusammenschau",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg4"],
    title: "Mosaikartige Zusammenschau",
    prompt:
      "Was bedeutet 'mosaikartige Zusammenschau' bei der Prüfung der erfinderischen Tätigkeit?",
    options: [
      {
        text: "Für die erfinderische Tätigkeit können mehrere SdT-Dokumente kombiniert werden — aber nur, wenn der Fachmann auch einen Anlass gehabt hätte, diese gerade so zu kombinieren",
        correct: true,
      },
      {
        text: "Der Prüfer kombiniert beliebig viele Dokumente des SdT, um den Anspruch zu Fall zu bringen",
        correct: false,
      },
      {
        text: "Mosaikartige Zusammenschau ist nur bei chemischen Erfindungen zulässig",
        correct: false,
      },
      {
        text: "Mehrere Dokumente dürfen bei der Prüfung erfinderischer Tätigkeit niemals kombiniert werden",
        correct: false,
      },
    ],
    explanation:
      "Anders als bei der Neuheit (Einzelvergleich) dürfen für die erfinderische Tätigkeit mehrere SdT-Dokumente kombiniert werden. Aber: Der Fachmann muss einen konkreten Anlass gehabt haben, gerade diese Kombination vorzunehmen. Willkürliches 'Zusammenstückeln' ist keine zulässige Argumentation für fehlendes Erfindergeist.",
  },

  {
    id: "da-patg1-026",
    topic: "§ 4 PatG — BGH vs. EPA Prüfschema",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg4", "psa"],
    title: "Problem-Lösungs-Ansatz: BGH vs. EPA",
    prompt:
      "Worin liegt der wesentliche Unterschied zwischen dem BGH-Prüfschema und dem Problem-Lösungs-Ansatz (PSA) des EPA bei der erfinderischen Tätigkeit?",
    options: [
      {
        text: "BGH prüft 'Naheliegen' ausgehend vom nächstliegenden SdT ohne formalisierten Dreischritt; EPA arbeitet strukturiert mit: (1) nächstliegendem SdT, (2) objektiver technischer Aufgabe, (3) could/would-Prüfung",
        correct: true,
      },
      {
        text: "BGH und EPA verwenden identische Prüfschemata, nur mit unterschiedlichen deutschen/englischen Bezeichnungen",
        correct: false,
      },
      {
        text: "EPA berücksichtigt keine objektive technische Aufgabe; BGH hingegen schon",
        correct: false,
      },
      {
        text: "BGH erlaubt Kombinationen mehrerer SdT-Dokumente nicht; EPA hingegen schon",
        correct: false,
      },
    ],
    explanation:
      "Das EPA arbeitet mit dem formalisierten Problem-Lösungs-Ansatz (PSA): (1) nächstliegender SdT identifizieren, (2) objektive technische Aufgabe formulieren (ggf. reformulieren), (3) Prüfen ob der Fachmann die Lösung in naheliegender Weise gefunden hätte. Der BGH prüft dagegen weniger formalistisch, fragt aber im Kern ähnliches: Würde der Fachmann ausgehend vom SdT auf die beanspruchte Lösung kommen?",
  },

  {
    id: "da-patg1-027",
    topic: "§ 4 PatG — Could/Would-Ansatz",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg4", "psa"],
    title: "Could/Would-Unterscheidung beim EPA",
    prompt:
      "Beim EPA-Problem-Lösungs-Ansatz wird zwischen 'could' und 'would' unterschieden. Was ist der entscheidende Unterschied?",
    options: [
      {
        text: "'Could' fragt ob der Fachmann die Lösung hätte finden können; 'would' fragt ob er angesichts des SdT auch tatsächlich Anlass gehabt hätte, sie zu finden — nur 'would' ist der maßgebliche Maßstab",
        correct: true,
      },
      {
        text: "'Could' bezieht sich auf technische Machbarkeit, 'would' auf wirtschaftliche Sinnhaftigkeit",
        correct: false,
      },
      {
        text: "Beide Maßstäbe sind äquivalent und werden beim EPA synonym verwendet",
        correct: false,
      },
      {
        text: "'Would' bezieht sich auf den konkreten Erfinder, 'could' auf den Fachmann als Kunstfigur",
        correct: false,
      },
    ],
    explanation:
      "Entscheidend ist das 'would': Der Fachmann muss ausgehend vom nächstliegenden SdT und in Kenntnis der objektiven technischen Aufgabe Anlass gehabt haben (motivated), genau diese Lösung zu wählen und dabei eine Erfolgsprognose gehabt haben. Dass er die Lösung theoretisch hätte finden können ('could'), reicht nicht — sonst wäre fast alles naheliegend.",
  },

  {
    id: "da-patg1-028",
    topic: "§ 4 PatG — Nächstliegender SdT",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg4", "psaClosest"],
    title: "Nächstliegender Stand der Technik",
    prompt:
      "Nach welchen Kriterien wird beim EPA-Problem-Lösungs-Ansatz der nächstliegende Stand der Technik ausgewählt?",
    options: [
      {
        text: "Das Dokument, das der beanspruchten Erfindung am ähnlichsten ist — es betrifft denselben Zweck oder dieselbe Wirkung und hat die meisten gemeinsamen Merkmale",
        correct: true,
      },
      {
        text: "Das älteste Dokument im SdT, das technisch verwandte Gegenstände betrifft",
        correct: false,
      },
      {
        text: "Das Dokument, das zuletzt vor dem Anmeldetag veröffentlicht wurde",
        correct: false,
      },
      {
        text: "Es werden stets alle Dokumente als gleichwertig betrachtet; eine Auswahl des 'nächstliegenden' ist nicht notwendig",
        correct: false,
      },
    ],
    explanation:
      "Der nächstliegende SdT wird nach dem Ähnlichkeitsprinzip ausgewählt: (1) demselben technischen Gebiet angehörend, (2) denselben Zweck/dieselbe Wirkung anstrebend, (3) die meisten gemeinsamen Merkmale mit der Erfindung aufweisend. Dies ist typischerweise ein einziges Dokument — von diesem Ausgangspunkt aus wird die objektive technische Aufgabe formuliert.",
  },

  {
    id: "da-patg1-029",
    topic: "§ 4 PatG — Aufgabe-Lösung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg4", "psaProblem"],
    title: "Objektive technische Aufgabe",
    prompt:
      "Warum ist die 'objektive' technische Aufgabe beim EPA-PSA oft nicht identisch mit der in der Patentanmeldung formulierten Aufgabe?",
    options: [
      {
        text: "Die Aufgabe wird auf Basis des tatsächlichen technischen Unterschieds zwischen beanspruchter Erfindung und nächstliegendem SdT reformuliert — die in der Anmeldung genannte Aufgabe kann zu weit oder zu eng gefasst sein oder eine unzulässige Lösung vorwegnehmen",
        correct: true,
      },
      {
        text: "Der Prüfer darf die Aufgabe nicht aus der Anmeldung übernehmen und muss stets eine neue formulieren",
        correct: false,
      },
      {
        text: "Die objektive Aufgabe wird vom Gericht festgelegt, nicht vom Prüfer",
        correct: false,
      },
      {
        text: "Anmeldungen müssen keine Aufgabe formulieren; der PSA rekonstruiert sie vollständig neu",
        correct: false,
      },
    ],
    explanation:
      "Die objektive technische Aufgabe wird aus dem technischen Effekt der Unterschiedsmerkmale (Erfindung minus nächstliegender SdT) abgeleitet. Enthält die ursprüngliche Aufgabe den Lösungsansatz, muss sie reformuliert werden ('Lösungsverbot'). Dies verhindert, dass Anmelder durch geschickte Aufgabenformulierung die Prüfung umgehen.",
  },

  {
    id: "da-patg1-030",
    topic: "§ 4 PatG — Sekundärindizien",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg4"],
    title: "Indizien für erfinderische Tätigkeit",
    prompt:
      "Welche Sekundärindizien können im deutschen Recht für das Vorliegen erfinderischer Tätigkeit sprechen?",
    options: [
      {
        text: "Kommerzieller Erfolg (wenn er auf dem erfinderischen Beitrag beruht), Überwindung eines Vorurteils im SdT, Lösung eines lang bekannten Problems",
        correct: true,
      },
      {
        text: "Kommerzieller Erfolg des Anmelders allgemein, unabhängig vom erfinderischen Beitrag",
        correct: false,
      },
      {
        text: "Die Tatsache, dass das Patent erteilt wurde, ist das stärkste Indiz für erfinderische Tätigkeit",
        correct: false,
      },
      {
        text: "Sekundärindizien sind im deutschen Patentrecht nicht anerkannt",
        correct: false,
      },
    ],
    explanation:
      "Sekundärindizien (Hilfserwägungen): (1) Langer Bedarf — Problem war lange bekannt, aber ungelöst; (2) Überwindung eines Fachvorurteils — der SdT lehrte, diesen Weg nicht zu gehen; (3) überraschende Wirkung; (4) kommerzieller Erfolg — wenn er kausal auf dem erfinderischen Beitrag beruht (nicht auf Marketing etc.). Diese Indizien können ein Naheliegen widerlegen.",
  },

  // ── § 5 PatG: Gewerbliche Anwendbarkeit ────────────────────────────────────

  {
    id: "da-patg1-031",
    topic: "§ 5 PatG — Gewerbliche Anwendbarkeit",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg5"],
    title: "Begriff gewerbliche Anwendbarkeit",
    prompt:
      "Wann ist eine Erfindung nach § 5 PatG gewerblich anwendbar?",
    options: [
      {
        text: "Wenn sie auf einem gewerblichen Gebiet hergestellt oder benutzt werden kann — gewerblich bedeutet jede wirtschaftliche Tätigkeit, einschließlich Landwirtschaft, aber ausschließlich ärztlicher Behandlungsmethoden",
        correct: true,
      },
      {
        text: "Wenn die Erfindung bereits kommerziell vermarktet wird",
        correct: false,
      },
      {
        text: "Wenn das Unternehmen des Anmelders mehr als 10 Mitarbeiter hat",
        correct: false,
      },
      {
        text: "Wenn die Erfindung profitabel hergestellt werden kann",
        correct: false,
      },
    ],
    explanation:
      "§ 5 PatG: Gewerblich anwendbar ist die Erfindung, wenn sie auf irgendeinem gewerblichen Gebiet hergestellt oder benutzt werden kann. 'Gewerblich' ist weit zu verstehen (auch Landwirtschaft, Fischerei). Ausgenommen sind Verfahren zur chirurgischen oder therapeutischen Behandlung des menschlichen oder tierischen Körpers (§ 5 Abs. 2 PatG).",
  },

  {
    id: "da-patg1-032",
    topic: "§ 5 PatG — Behandlungsmethoden",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg5"],
    title: "Medizinische Behandlungsmethoden",
    prompt:
      "Warum sind chirurgische und therapeutische Behandlungsmethoden nach § 5 Abs. 2 PatG von der Patentierbarkeit ausgeschlossen?",
    options: [
      {
        text: "Um zu verhindern, dass Ärzte bei der Ausübung ihres Berufs durch Patente behindert werden — die freie Ausübung der Heilkunde hat Vorrang vor dem Patentschutz",
        correct: true,
      },
      {
        text: "Weil medizinische Methoden keine technische Lehre enthalten",
        correct: false,
      },
      {
        text: "Weil Krankenkassen die Kosten für patentierte Behandlungen nicht tragen würden",
        correct: false,
      },
      {
        text: "Medizinische Behandlungsmethoden sind als sittenwidrig nach § 2 PatG ausgeschlossen",
        correct: false,
      },
    ],
    explanation:
      "Der Ausschluss dient dem öffentlichen Interesse: Ärzte und Heilpraktiker sollen bei der Behandlung von Patienten nicht durch Patentrechte Dritter eingeschränkt werden. Nur Behandlungsmethoden sind ausgeschlossen — medizinische Vorrichtungen, diagnostische Verfahren (außer direkt am Körper) und pharmazeutische Erzeugnisse sind hingegen patentierbar.",
  },

  {
    id: "da-patg1-033",
    topic: "§ 5 PatG — Diagnostische Verfahren",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg5"],
    title: "Abgrenzung Diagnose / Therapie",
    prompt:
      "Ein Anspruch lautet: 'Verfahren zur Diagnose von Diabetes mellitus durch Messen des Blutzuckerspiegels direkt am Patienten'. Ist dieser Anspruch patentierbar?",
    options: [
      {
        text: "Nein — direkt am menschlichen Körper durchgeführte diagnostische Verfahren sind nach § 5 Abs. 2 PatG (analog Art. 53(c) EPÜ) nicht patentierbar",
        correct: true,
      },
      {
        text: "Ja — diagnostische Verfahren sind explizit patentierbar, nur therapeutische sind ausgeschlossen",
        correct: false,
      },
      {
        text: "Ja — solange kein Eingriff in den Körper erfolgt (nicht-invasiv), sind alle diagnostischen Verfahren patentierbar",
        correct: false,
      },
      {
        text: "Nein — wegen fehlender gewerblicher Anwendbarkeit",
        correct: false,
      },
    ],
    explanation:
      "Auch diagnostische Verfahren, die am lebenden Tier/Menschen praktiziert werden, sind ausgeschlossen — wenn sie alle Schritte umfassen, die für eine Diagnose notwendig sind. Reine In-vitro-Diagnose (z.B. Bluttest im Labor) oder ein Gerät zur Diagnose sind hingegen patentierbar. § 5 Abs. 2 Satz 2 PatG stellt klar: Erzeugnisse (inkl. Medikamente) sind nicht ausgeschlossen.",
  },

  // ── Verfahrensfragen ────────────────────────────────────────────────────────

  {
    id: "da-patg1-034",
    topic: "§ 1 PatG — Prüfungsreihenfolge",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg1"],
    title: "Reihenfolge der Patentierbarkeitsmerkmale",
    prompt:
      "In welcher Reihenfolge werden die Patentierbarkeitsvoraussetzungen sinnvollerweise geprüft?",
    options: [
      {
        text: "Erst: Liegt überhaupt eine Erfindung vor (Technizität, Negativkatalog)? Dann: Neuheit (§ 3)? Dann: Erfinderische Tätigkeit (§ 4)? Zuletzt: Gewerbliche Anwendbarkeit (§ 5)?",
        correct: true,
      },
      {
        text: "Zuerst Gewerbliche Anwendbarkeit, dann Neuheit, dann Erfinderische Tätigkeit, zuletzt Erfindungsbegriff",
        correct: false,
      },
      {
        text: "Die Reihenfolge ist gesetzlich nicht vorgegeben und spielt keine Rolle",
        correct: false,
      },
      {
        text: "Erfinderische Tätigkeit wird zuerst geprüft, weil sie das strengste Kriterium ist",
        correct: false,
      },
    ],
    explanation:
      "Logische Prüfungsreihenfolge: (1) Technizität/Erfindungsbegriff — ohne Erfindung nützt der Rest nichts; (2) Neuheit — wenn nicht neu, keine erfinderische Tätigkeit prüfbar; (3) Erfinderische Tätigkeit; (4) Gewerbliche Anwendbarkeit — meist unproblematisch, aber prüftaktisch sinnvoll zuletzt. Dies spart Prüfaufwand bei frühzeitigem Scheitern.",
  },

  {
    id: "da-patg1-035",
    topic: "§ 1 PatG — Falllösung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg1", "patg3", "patg4"],
    title: "Fallanalyse: Patentierbarkeit — Softwareerfindung",
    prompt:
      "Anmelderin beansprucht: 'Verfahren zur Optimierung von Lieferketten durch Anwendung des Simplex-Algorithmus auf Transportdaten'. Der Algorithmus ist aus der Mathematik bekannt; die Anwendung auf Lieferketten-Software ist neu. Wie ist die Patentierbarkeit zu beurteilen?",
    options: [
      {
        text: "Scheitert an § 1 Abs. 3 PatG: Als mathematisches Verfahren (Simplex-Algorithmus) und Methode für geschäftliche Tätigkeiten (Lieferkettenoptimierung) ist der Gegenstand 'als solcher' ausgeschlossen — sofern kein über die Datenverwaltung hinausgehender technischer Effekt beansprucht wird",
        correct: true,
      },
      {
        text: "Patentierbar — die Neuheit der Anwendung des Algorithmus auf Lieferketten begründet Patentierbarkeit",
        correct: false,
      },
      {
        text: "Patentierbar — Softwareverfahren sind im deutschen Patentrecht nach neuerer Rechtsprechung grundsätzlich patentierbar",
        correct: false,
      },
      {
        text: "Scheitert nicht an § 1, aber an § 4 PatG wegen fehlender erfinderischer Tätigkeit",
        correct: false,
      },
    ],
    explanation:
      "Der Anspruch beschreibt einen mathematischen Algorithmus (Simplex) angewendet auf eine betriebswirtschaftliche Aufgabe (Lieferkettenoptimierung). Ohne einen über die reine Datenverarbeitung hinausgehenden technischen Effekt (z.B. physische Steuerung von Transportmitteln) scheitert der Anspruch am Negativkatalog § 1 Abs. 3 PatG. Neuheit allein macht aus einer ausgeschlossenen Methode keine patentierbare Erfindung.",
  },

  {
    id: "da-patg1-036",
    topic: "§ 3 PatG — Neuheitsschädliche Offenbarung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg3"],
    title: "Was ist offenbart?",
    prompt:
      "Dokument D1 zeigt eine Vorrichtung mit Merkmal A, B und C. Die Erfindung beansprucht A, B, C und D. Wie ist die Neuheitssituation?",
    options: [
      {
        text: "Der Anspruch ist neu — D1 offenbart nicht Merkmal D, daher sind nicht alle Merkmale des Anspruchs in einem Dokument offenbart",
        correct: true,
      },
      {
        text: "Der Anspruch ist nicht neu — der Fachmann würde D aus A, B, C ableiten",
        correct: false,
      },
      {
        text: "Die Neuheit hängt davon ab, ob D eine wesentliche Verbesserung gegenüber A+B+C darstellt",
        correct: false,
      },
      {
        text: "Der Anspruch ist nicht neu, wenn D auf dem Gebiet des SdT bekannt ist",
        correct: false,
      },
    ],
    explanation:
      "Neuheit erfordert, dass alle Merkmale des Anspruchs in einem einzigen Dokument des SdT offenbart sind. Fehlt auch nur ein Merkmal in D1 (hier: Merkmal D), ist der Anspruch neu. Ob der Fachmann D aus A+B+C hätte ableiten können, ist eine Frage der erfinderischen Tätigkeit, nicht der Neuheit.",
  },

  {
    id: "da-patg1-037",
    topic: "§ 4 PatG — Fehlende erfinderische Tätigkeit",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg4"],
    title: "Argumentation fehlende erfinderische Tätigkeit",
    prompt:
      "Wie baut man eine Argumentation auf, die fehlende erfinderische Tätigkeit begründet?",
    options: [
      {
        text: "(1) Nächstliegenden SdT benennen; (2) Unterschied zur Erfindung identifizieren; (3) aus dem Unterschied die objektive technische Aufgabe ableiten; (4) zeigen, dass der Fachmann ausgehend vom SdT Motivation + Erfolgsprognose für genau diese Lösung gehabt hätte",
        correct: true,
      },
      {
        text: "Zeigen, dass die Erfindung bereits in einem Dokument des SdT offenbart ist",
        correct: false,
      },
      {
        text: "Nachweisen, dass der Erfinder selbst keine überdurchschnittliche Qualifikation hat",
        correct: false,
      },
      {
        text: "Darauf hinweisen, dass die Erfindung wirtschaftlich wenig Mehrwert bringt",
        correct: false,
      },
    ],
    explanation:
      "Die Standardargumentation: (1) Nächstliegender SdT (D1); (2) Unterschiedsmerkmale; (3) objektive technische Aufgabe (was das Unterschiedsmerkmal löst); (4) Motivation-Brücke: SdT (D1 + ggf. D2) + Allgemeinwissen geben dem Fachmann Anlass, genau so vorzugehen. Fehlt Schritt (4) — das ist der häufigste Fehler in Argumentationen.",
  },

  {
    id: "da-patg1-038",
    topic: "§ 1 PatG — Computerprogramm mit technischer Wirkung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg1"],
    title: "Technische Wirkung bei Softwareansprüchen",
    prompt:
      "Ein Anspruch lautet: 'Computerprogramm, das beim Laufen auf einem Prozessor die Kühlleistung eines Rechenzentrumsservers um 30% reduziert'. Ist dieser Anspruch dem Grunde nach patentierbar?",
    options: [
      {
        text: "Ja — das Programm erzielt eine technische Wirkung (Wärmemanagement/Energieeinsparung), die über die normale Datenverarbeitung hinausgeht und es nicht 'als solches' ausgeschlossen ist",
        correct: true,
      },
      {
        text: "Nein — Computerprogramme sind nach § 1 Abs. 3 Nr. 3 PatG immer ausgeschlossen",
        correct: false,
      },
      {
        text: "Nein — der Anspruch muss die Hardware spezifizieren, Software allein kann nicht beansprucht werden",
        correct: false,
      },
      {
        text: "Ja, aber nur wenn die Energieeinsparung auch ohne den Computer gemessen werden kann",
        correct: false,
      },
    ],
    explanation:
      "Software mit einer 'weiteren technischen Wirkung' (beyond the normal physical interactions between program and computer) ist nicht 'als solche' ausgeschlossen. Wärmemanagement, Netzwerklatenz-Reduktion, Kompressionsverbesserungen mit physischer Wirkung gelten als technische Effekte. Dies ist die Kernlinie der BGH- und EPA-Rechtsprechung zu Softwarepatenten.",
  },

  {
    id: "da-patg1-039",
    topic: "§ 3 PatG — Nachveröffentlichter SdT",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg3"],
    title: "§ 3 Abs. 2 — Nachveröffentlichter SdT",
    prompt:
      "Anmeldung A hat Anmeldetag 1.1.2025, Anmeldung B hat Anmeldetag 1.3.2025 und Veröffentlichungsdatum 1.9.2025. Anmeldung B offenbart den Gegenstand von A vollständig. Welche Auswirkung hat B auf A?",
    options: [
      {
        text: "B kann nach § 3 Abs. 2 PatG als nachveröffentlichter SdT der Neuheit von A nicht schaden — B hat einen späteren Anmeldetag als A",
        correct: true,
      },
      {
        text: "B schadet der Neuheit von A, weil B vor der Erteilung von A veröffentlicht wurde",
        correct: false,
      },
      {
        text: "B ist als nachveröffentlichter SdT relevant und schadet sowohl der Neuheit als auch der erfinderischen Tätigkeit von A",
        correct: false,
      },
      {
        text: "B hat keine Auswirkung auf A, weil A früher angemeldet wurde",
        correct: false,
      },
    ],
    explanation:
      "§ 3 Abs. 2 PatG: Nachveröffentlichter SdT sind Anmeldungen mit früherem Anmeldetag als der zu prüfenden Anmeldung, die erst später veröffentlicht wurden. Da B (Anmeldetag 1.3.2025) einen späteren Anmeldetag als A (1.1.2025) hat, ist B kein nachveröffentlichter SdT für A. Die Schutzrechtslage ist umgekehrt: A könnte für B relevant sein.",
  },

  {
    id: "da-patg1-040",
    topic: "§ 4 PatG — Einzel- vs. Kombinationsüberlegung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg4"],
    title: "Einzeldokument vs. Kombination bei § 4",
    prompt:
      "Dokument D1 zeigt A+B, Dokument D2 zeigt C. Die Erfindung beansprucht A+B+C. Für die Prüfung erfinderischer Tätigkeit: Ist die Kombination D1+D2 grundsätzlich zulässig?",
    options: [
      {
        text: "Ja — anders als bei der Neuheit dürfen für § 4 mehrere Dokumente kombiniert werden, vorausgesetzt der Fachmann hätte Anlass gehabt, gerade D1 und D2 zu kombinieren",
        correct: true,
      },
      {
        text: "Nein — auch für § 4 gilt das Einzeldokumentprinzip",
        correct: false,
      },
      {
        text: "Ja — die Kombination ist immer zulässig, wenn D1 und D2 dasselbe Fachgebiet betreffen",
        correct: false,
      },
      {
        text: "Die Kombination ist nur zulässig, wenn D1 explizit auf D2 verweist",
        correct: false,
      },
    ],
    explanation:
      "Bei § 4 (erfinderische Tätigkeit) ist die mosaikartige Zusammenschau zulässig: mehrere SdT-Dokumente können kombiniert werden. Aber: Der Fachmann muss einen Anlass gehabt haben, D1 und D2 gerade so zu kombinieren — das ist der entscheidende Prüfpunkt. Beliebige Kombinationen (ohne Motivation) führen nicht zu fehlender erfinderischer Tätigkeit.",
  },

  {
    id: "da-patg1-041",
    topic: "§ 1 PatG — Ästhetische Formschöpfungen",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 1,
    sources: ["patg1"],
    title: "Ästhetische Formschöpfung vs. Design",
    prompt:
      "Welchem Schutzrecht unterliegen ästhetische Formschöpfungen, die gemäß § 1 Abs. 3 Nr. 2 PatG vom Patentschutz ausgeschlossen sind?",
    options: [
      { text: "Designrecht (DesignG) und/oder Urheberrecht, je nach Individualität der Gestaltung", correct: true },
      { text: "Markenrecht, wenn die Form als Marke eintragungsfähig ist", correct: false },
      { text: "Ästhetische Formschöpfungen genießen in Deutschland keinerlei Schutz", correct: false },
      { text: "Gebrauchsmusterrecht — Gebrauchsmuster schützen ästhetische Formen", correct: false },
    ],
    explanation:
      "Ästhetische Formschöpfungen sind vom Patentschutz ausgeschlossen, aber nicht schutzlos: Das DesignG schützt neuartige und eigenartige zweidimensionale/dreidimensionale Erscheinungsbilder; das UrhG schützt Werke der angewandten Kunst bei ausreichender Schöpfungshöhe. Der Ausschluss aus dem PatG bedeutet nicht fehlenden Rechtsschutz, sondern Verweis auf das richtige Schutzrecht.",
  },

  {
    id: "da-patg1-042",
    topic: "§ 4 PatG — Überwindung eines Fachvorurteils",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg4"],
    title: "Fachvorurteil als Indiz für erfinderische Tätigkeit",
    prompt:
      "Der SdT lehrte über Jahrzehnte, dass Material X für Hochtemperaturanwendungen ungeeignet sei. Der Anmelder zeigt, dass X durch eine neue Beschichtung bei 800°C einsetzbar ist. Welche Bedeutung hat dies für die erfinderische Tätigkeit?",
    options: [
      {
        text: "Die Überwindung eines Fachvorurteils ist ein starkes Indiz für erfinderische Tätigkeit — der Fachmann hätte diesen Weg nicht versucht, weil der SdT dagegen sprach",
        correct: true,
      },
      {
        text: "Das Vorurteil ist irrelevant — es kommt nur auf den objektiven SdT an, nicht auf subjektive Einschätzungen",
        correct: false,
      },
      {
        text: "Fachvorurteile belegen fehlende erfinderische Tätigkeit, weil die Lösung offensichtlich sein muss, wenn sie doch gefunden wurde",
        correct: false,
      },
      {
        text: "Fachvorurteile sind nur in chemischen Patentverfahren anerkannte Indizien",
        correct: false,
      },
    ],
    explanation:
      "Ein Fachvorurteil im SdT ist eines der stärksten Indizien für erfinderische Tätigkeit: Der Fachmann hätte den Weg aktiv gemieden, weil der SdT dagegen sprach. Wer trotzdem diesen Weg geht und Erfolg hat, hat einen echten Erfindungsschritt gemacht. Dies ist ein klassisches Sekundärindiz, das auch das EPA in der Prüfung berücksichtigt.",
  },

  {
    id: "da-patg1-043",
    topic: "§ 3 PatG — Benutzungsoffenbarung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg3"],
    title: "Öffentliche Benutzung als SdT",
    prompt:
      "Ein Produkt wurde vor dem Anmeldetag auf dem Markt verkauft. Der Käufer ist jedoch an eine Geheimhaltungsvereinbarung (NDA) gebunden. Ist der Verkauf neuheitsschädlich?",
    options: [
      {
        text: "Nein — wenn der Käufer einer wirksamen Geheimhaltungspflicht unterliegt, wurde das Produkt der Öffentlichkeit nicht zugänglich gemacht",
        correct: true,
      },
      {
        text: "Ja — jeder kommerzielle Verkauf ist neuheitsschädlich, unabhängig von Geheimhaltungsvereinbarungen",
        correct: false,
      },
      {
        text: "Nein — Verkäufe an Endkunden begründen nie eine Offenbarung im Sinne des § 3 PatG",
        correct: false,
      },
      {
        text: "Ja — NDAs schützen nicht gegen den Verlust der Neuheit, weil das Produkt körperlich übergeben wurde",
        correct: false,
      },
    ],
    explanation:
      "Entscheidend für § 3 PatG ist die Zugänglichkeit 'für die Öffentlichkeit'. Eine Benutzung unter Geheimhaltungspflicht (NDA) ist kein öffentlich zugängliches Wissen. Das Produkt ist dem Käufer bekannt, aber nicht 'der Öffentlichkeit'. Bei Verstoß gegen das NDA greifen die Vorschriften über unschädliche Offenbarungen (§ 3 Abs. 5 PatG).",
  },

  {
    id: "da-patg1-044",
    topic: "§ 1 PatG — Entdeckung vs. Erfindung II",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg1"],
    title: "Neue chemische Verbindung",
    prompt:
      "Ein Forscher synthetisiert erstmals eine Verbindung, die in der Natur nicht vorkommt. Handelt es sich um eine Entdeckung oder eine Erfindung?",
    options: [
      {
        text: "Erfindung — die Verbindung existierte nicht in der Natur und wurde durch menschlichen technischen Eingriff geschaffen",
        correct: true,
      },
      {
        text: "Entdeckung — alle Verbindungen, die theoretisch möglich sind, waren latent in der Natur vorhanden",
        correct: false,
      },
      {
        text: "Entdeckung, weil die Eigenschaften der Verbindung durch Naturgesetze vorherbestimmt sind",
        correct: false,
      },
      {
        text: "Weder Erfindung noch Entdeckung — chemische Verbindungen sind eigene Schutzrechtskategorie",
        correct: false,
      },
    ],
    explanation:
      "Eine neu synthetisierte Verbindung, die in der Natur nicht vorkommt, ist eine Erfindung: Der Mensch hat durch technischen Eingriff etwas Neues geschaffen. Dagegen ist die Erkenntnis einer natürlichen Eigenschaft einer bereits existierenden Substanz eine Entdeckung. Die Abgrenzung hängt davon ab, ob der Stoff in der Natur (in zugänglicher Form) existierte.",
  },

  {
    id: "da-patg1-045",
    topic: "§ 4 PatG — Problem-Lösungs-Ansatz Anwendung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg4", "psa", "psaProblem"],
    title: "PSA — Schritt für Schritt",
    prompt:
      "Erfindung: Schraube mit neuartigem Gewinde, das 20% mehr Haltekraft bei Vibrationen bietet. Nächstliegender SdT D1: Schraube mit Standardgewinde. Was ist die objektive technische Aufgabe?",
    options: [
      {
        text: "Bereitstellung einer Schraube mit verbesserter Haltekraft unter Vibrationsbelastung — formuliert als technische Aufgabe ohne die Lösung (neuartiges Gewinde) vorwegzunehmen",
        correct: true,
      },
      {
        text: "Entwicklung eines Gewindes, das 20% mehr Haltekraft bietet — die Lösung muss in der Aufgabe spezifiziert sein",
        correct: false,
      },
      {
        text: "Verbesserung von Schrauben allgemein — die Aufgabe muss möglichst weit gefasst sein",
        correct: false,
      },
      {
        text: "Beseitigung des im SdT geschilderten Problems mit Standardgewinden unter Last",
        correct: false,
      },
    ],
    explanation:
      "Die objektive technische Aufgabe wird aus dem technischen Effekt des Unterschiedsmerkmals formuliert, aber ohne die Lösung vorwegzunehmen ('Lösungsverbot'). Korrekt: 'Verbesserung der Verbindungsstabilität unter Vibrationsbelastung'. Falsch wäre: 'Bereitstellung eines verbesserten Gewindes' — das wäre die Lösung.",
  },

  {
    id: "da-patg1-046",
    topic: "§ 2a PatG — Mikrobiologische Verfahren",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg2a"],
    title: "Ausnahme für mikrobiologische Verfahren",
    prompt:
      "Ein Verfahren zur Herstellung eines Antibiotikums durch Fermentation eines Bakterienstamms soll patentiert werden. Fällt es unter den Ausschluss des § 2a PatG?",
    options: [
      {
        text: "Nein — mikrobiologische Verfahren und die durch sie gewonnenen Erzeugnisse sind nach § 2a Abs. 2 Nr. 1 PatG ausdrücklich patentierbar",
        correct: true,
      },
      {
        text: "Ja — biologische Verfahren sind alle nach § 2a PatG ausgeschlossen",
        correct: false,
      },
      {
        text: "Ja — Bakterien sind Lebewesen, deren Verwendung zur Herstellung unter den Ausschluss fällt",
        correct: false,
      },
      {
        text: "Nein, aber nur wenn der Bakterienstamm gentechnisch verändert wurde",
        correct: false,
      },
    ],
    explanation:
      "§ 2a Abs. 2 Nr. 1 PatG nimmt mikrobiologische Verfahren und ihre Erzeugnisse ausdrücklich aus dem Ausschluss aus. Fermentationsverfahren (Herstellung von Antibiotika, Enzymen, Vitaminen etc.) durch Mikroorganismen sind daher patentierbar. Der Ausschluss betrifft nur 'im Wesentlichen biologische' Züchtungsverfahren für Pflanzen und Tiere.",
  },

  {
    id: "da-patg1-047",
    topic: "§ 1 PatG — Praxisfall: KI/Machine Learning",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg1"],
    title: "KI-Erfindungen und Technizität",
    prompt:
      "Ein neuronales Netz klassifiziert Röntgenbilder zur Krebsfrüherkennung mit höherer Genauigkeit als bisherige Methoden. Der Anspruch lautet: 'Computerimplementiertes Verfahren zur Klassifikation von Röntgenbildern auf Basis eines trainierten neuronalen Netzes'. Wie ist die Technizität zu beurteilen?",
    options: [
      {
        text: "Grundsätzlich patentierbar — die Klassifikation medizinischer Bilddaten ist ein technischer Prozess; die technische Wirkung (verbesserte Diagnosegenauigkeit aus technischen Daten) geht über reine Datenverarbeitung hinaus",
        correct: true,
      },
      {
        text: "Nicht patentierbar — neuronale Netze sind mathematische Modelle und daher als solche ausgeschlossen",
        correct: false,
      },
      {
        text: "Nicht patentierbar — diagnostische Verfahren am Menschen sind nach § 5 Abs. 2 PatG ausgeschlossen",
        correct: false,
      },
      {
        text: "Patentierbar, aber nur wenn das neuronale Netz auf eigener spezieller Hardware läuft",
        correct: false,
      },
    ],
    explanation:
      "KI-Anwendungen für technische Daten (Röntgenbilder = technische Messdaten) werden zunehmend als patentierbar anerkannt. Die technische Wirkung liegt in der Verbesserung eines technischen Prozesses (Bildanalyse). Anders wäre es, wenn die KI rein betriebswirtschaftliche Entscheidungen träfe. Der Ausschluss für diagnostische Verfahren (§ 5 Abs. 2) greift nur bei der direkten Ausübung am Patienten, nicht bei computergestützter Bildauswertung.",
  },

  {
    id: "da-patg1-048",
    topic: "§ 3 PatG — Implizite Offenbarung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 3,
    sources: ["patg3"],
    title: "Implizite Neuheitsoffenbarung",
    prompt:
      "D1 beschreibt ein Verfahren X, das notwendigerweise ein Merkmal M erzeugt, auch wenn M in D1 nicht erwähnt wird. Der Anmelder beansprucht Verfahren X + Merkmal M. Ist der Anspruch neu?",
    options: [
      {
        text: "Nein — implizit offenbarte Merkmale (die zwingend aus der SdT-Lehre folgen) gelten als offenbart und sind neuheitsschädlich",
        correct: true,
      },
      {
        text: "Ja — nur explizit genannte Merkmale gelten als offenbart",
        correct: false,
      },
      {
        text: "Ja — da M in D1 unbekannt war, ist M ein neues Merkmal",
        correct: false,
      },
      {
        text: "Die Frage der impliziten Offenbarung wird nur bei der erfinderischen Tätigkeit berücksichtigt",
        correct: false,
      },
    ],
    explanation:
      "Implizite Offenbarung: Merkmale, die zwangsläufig aus der Ausführung des SdT resultieren (auch wenn nicht explizit genannt), gelten als offenbart. Wenn Verfahren X notwendig Merkmal M erzeugt, hat der Fachmann M 'erhalten', auch ohne es zu wissen. Dies ist eine der trickreichsten Neuheitsfragen und erfordert präzise technische Analyse.",
  },

  {
    id: "da-patg1-049",
    topic: "§ 4 PatG — Routinemäßige Optimierung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "single",
    level: 2,
    sources: ["patg4"],
    title: "Routinemäßige Optimierung vs. Erfindung",
    prompt:
      "Der SdT offenbart ein Verfahren mit Temperaturbereich 50–200°C. Der Anmelder beansprucht den Bereich 120–140°C als optimalen Bereich. Liegt erfinderische Tätigkeit vor?",
    options: [
      {
        text: "Nein — das Optimieren bekannter Parameter durch routinemäßige Versuche ist keine erfinderische Tätigkeit; der Fachmann würde den optimalen Bereich durch Standardexperimente finden",
        correct: true,
      },
      {
        text: "Ja — der spezifische Temperaturbereich wurde im SdT nicht beschrieben und ist daher erfinderisch",
        correct: false,
      },
      {
        text: "Ja — wenn der Bereich 120–140°C eine überraschende Wirkung zeigt",
        correct: false,
      },
      {
        text: "Nein — wegen fehlender Neuheit (Bereich liegt innerhalb des SdT)",
        correct: false,
      },
    ],
    explanation:
      "Routinemäßige Parameteroptimierung innerhalb eines bekannten SdT-Bereichs ist keine erfinderische Tätigkeit: Der Fachmann würde durch Standard-Screening den optimalen Bereich finden. Liegt der optimale Bereich jedoch außerhalb des im SdT offenbarten Bereichs oder zeigt er eine überraschende, im SdT nicht erwartete Wirkung, kann erfinderische Tätigkeit vorliegen. Hier ist der Bereich 120–140°C eine Teilmenge von 50–200°C ohne überraschende Wirkung.",
  },

  {
    id: "da-patg1-050",
    topic: "§ 1 PatG — Zusammenfassung",
    mode: "ausbildung",
    reviewStatus: "beta-ready",
    kind: "allnone",
    level: 2,
    sources: ["patg1", "patg3", "patg4", "patg5"],
    title: "Patentierbarkeit — Kurzcheck",
    prompt:
      "Bewerte: 'Eine neue Art des Kochens von Pasta, bei der man zuerst Wasser zum Kochen bringt und dann die Pasta hineintut, beansprucht als Verfahren. Dies wurde bisher nur mündlich in Kochkursen gelehrt.' Patentierbar?",
    options: [
      {
        text: "Nicht patentierbar — fehlt am Erfindungsbegriff (keine technische Lehre im patentrechtlichen Sinne) UND an der Neuheit (mündliche Lehre in Kochkursen ist öffentliche Zugänglichkeit nach § 3 Abs. 1 PatG)",
        correct: true,
      },
      {
        text: "Patentierbar — ein Kochverfahren hat technischen Charakter (Wärmebehandlung von Lebensmitteln) und ist damit eine Erfindung",
        correct: false,
      },
      {
        text: "Nicht patentierbar — nur wegen fehlender Neuheit (mündliche Offenbarung), die Technizität wäre gegeben",
        correct: false,
      },
      {
        text: "Patentierbar — mündliche Kochkurse begründen keine öffentliche Zugänglichkeit im Sinne von § 3 PatG",
        correct: false,
      },
    ],
    explanation:
      "Doppeltes Problem: (1) Technizität: Das beschriebene Verfahren ist eine triviale Kochpraxis, die keine Naturkräfte zur Herbeiführung eines übersehbaren Erfolgs nutzbar macht. (2) Neuheit: § 3 Abs. 1 PatG schließt auch mündliche Beschreibungen als SdT ein — 'durch mündliche Beschreibung [...] zugänglich gemacht'. Kochkurse sind eine solche mündliche Offenbarung.",
  },
];
